import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod, resolveComparePeriod } from '../../components/performance/pmAnalyticsFilters';
import KpiCards from './components/KpiCards';
import { StageCompletionPanel, StageTaskDistributionPanel } from './components/StagePanels';
import { OverdueDistributionPanel, OverdueTasksPanel, UnitCompletionPanel } from './components/OverduePanels';
import { ActionTrackingPanel, AiInsightsPanel, ReviewAttentionPanel } from './components/InsightPanels';
import { Grid, PageEmpty, PageWrap } from './styles';
import { anyStale, fetchPeriods, oldestCalculatedAt } from './config';

// PM Analitik Faz 2 Plan 3 (spec §6) — İK Operasyon Panosu (/performance/operations; eski "Performans Paneli"nin yerine).
// Yalnız İK (rota + menü requiresEnterpriseAdmin, PM aboneliği + koltuk). Süzgeçler URL'dedir (geri dönüşte korunur); her panel
// kendi isteğini atar ve kendi hata/iskelet durumunu taşır. Sayfa gövdesi tek kaydırmadır (sayfada DataTable yok).
export default function PerformanceOperationsPage() {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [metas, setMetas] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    return () => { alive = false; };
  }, []);

  // Dönem zorunlu ve temizlenemez: URL'de yoksa (ya da artık yoksa) en yeni dönem seçilir.
  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  // Süzgeç değişince eski panellerin güncellik bilgisi düşer.
  const filterKey = JSON.stringify(filters);
  useEffect(() => { setMetas({}); }, [filterKey]);
  const onMeta = useCallback((key, meta) => setMetas((m) => ({ ...m, [key]: meta })), []);
  const onChanged = useCallback(() => setRefreshKey((k) => k + 1), []);

  const comparePeriod = useMemo(() => resolveComparePeriod(periods, filters), [periods, filters]);
  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; yukarıdaki etki en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);

  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t('performance.operations.title') }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.operations.noPeriods')}</PageEmpty> : null}
      {periods.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={oldestCalculatedAt(metas)}
          stale={anyStale(metas)}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready ? (
        <Grid key={filters.periodId}>
          <KpiCards filters={filters} comparePeriod={comparePeriod} onMeta={onMeta} />
          <StageCompletionPanel filters={filters} onMeta={onMeta} />
          <StageTaskDistributionPanel filters={filters} onMeta={onMeta} />
          <OverdueTasksPanel filters={filters} refreshKey={refreshKey} onChanged={onChanged} />
          <OverdueDistributionPanel filters={filters} onMeta={onMeta} />
          <UnitCompletionPanel filters={filters} onMeta={onMeta} />
          <ReviewAttentionPanel filters={filters} refreshKey={refreshKey} onChanged={onChanged} />
          <ActionTrackingPanel filters={filters} refreshKey={refreshKey} />
          <AiInsightsPanel filters={filters} refreshKey={refreshKey} onChanged={onChanged} />
        </Grid>
      ) : null}
    </PageWrap>
  );
}
